import React, {ConsumerProps, useContext, useRef} from 'react';
import {useRouter} from 'next/router';

import {SegmentAnalyticsBackend, SegmentAnalyticTrackPayload} from './SegmentAnalyticsBackend';
import {UserIdentificationProvider} from './UserIdentificationProvider';

import {AnalyticsConsumerOptions, AnalyticsContext, GenericEvent} from './AnalyticsProvider.types';

const analyticsContext = React.createContext<AnalyticsContext>({
  track: () => Promise.resolve(),
});

export const useAnalytics = (contextOpts?: AnalyticsConsumerOptions): AnalyticsContext => {
  const {track} = useContext(analyticsContext);

  // Use an "inner" context value and an "outer" context value where methods in
  // the outer value would simply call the methods in the inner value.
  // The reason for having these 2 sets of value is so that this hook function
  // can return the outer value, and because the outer value is NEVER updated,
  // the consumers of this hook will never see any update and thus would not
  // have rerender triggered.
  const innerContextValue = useRef<AnalyticsContext>({track});
  const outerContextValue = useRef<AnalyticsContext>({
    track: payload => innerContextValue.current.track(payload),
  });

  // When we do need to update the context value, we only update the "inner"
  // value (which the "outer" value calls). This way we get updated context
  // values without triggering rerender in the consumers.
  innerContextValue.current.track = payload =>
    track<GenericEvent>({context: contextOpts?.context, ...((payload as any) || {})});

  return outerContextValue.current;
};

export const AnalyticsConsumer: React.FC<
  AnalyticsConsumerOptions & ConsumerProps<AnalyticsContext>
> = ({context: trackEventContext, children}) => (
  <analyticsContext.Consumer>
    {({track}) => {
      return children({
        track: payload => {
          return track<GenericEvent>({context: trackEventContext, ...((payload as any) || {})});
        },
      });
    }}
  </analyticsContext.Consumer>
);

export const AnalyticsProvider = ({children}: {children?: React.ReactNode}) => {
  const router = useRouter();

  return (
    <SegmentAnalyticsBackend>
      {({track: trackWithSegment}) => {
        const contextValue: AnalyticsContext = {
          track: payload => {
            // Inject page path into payload before sending data to Segment.
            (payload as GenericEvent).pagePath = router.asPath;

            return trackWithSegment(payload as any as SegmentAnalyticTrackPayload);
          },
        };

        return (
          <analyticsContext.Provider value={contextValue}>
            <UserIdentificationProvider>{children}</UserIdentificationProvider>
          </analyticsContext.Provider>
        );
      }}
    </SegmentAnalyticsBackend>
  );
};
